import { useRef, useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { iaApi } from '../../api/resources';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { libelleTaux } from '../../lib/caisse';
import { prepareFichiers } from '../../lib/document-ia';
import { liensEnvoi } from '../../lib/envoi';
import { messageIa } from '../../lib/ia';
import { CLE_PACK_IA, packIaActif } from '../../lib/pack-ia';
import { printDoc } from '../../lib/print';
import { tauxTvaParDefaut } from '../../lib/tva';
import {
  CLE_CORPS_METIER,
  METIERS_SOUMISSION,
  brouillonDepuisLecture,
  gardeBrouillon,
  libelleMetier,
  ligneVierge,
  majorePrix,
  messageOffre,
  montantLigne,
  oublieBrouillon,
  prestationsAApprendre,
  prixASaisir,
  reprendBrouillon,
  resumeReglage,
  totauxSoumission,
  verifieFichiers,
  versDevis,
} from '../../lib/soumission';
import { C } from '../../theme/palette';
import { ApercuSoumission } from './ApercuSoumission.jsx';
import { CorpsMetierCard } from './CorpsMetierCard.jsx';
import { ImportVerrouille } from './ImportVerrouille.jsx';
import { pastille } from './styles';
import { RapportVigilance } from './RapportVigilance.jsx';
import { termesOffre } from '../../lib/terminologie';

const EN_COURS = 'lecture';

/** Fichiers d'échange et PDF acceptés par le premier bouton. */
const TYPES_DOCUMENT =
  '.pdf,application/pdf,.sia,.01s,.02s,.03s,.d81,.d83,.d84,.x83,.x84,.txt,.xml';

const fr = (n) =>
  (+n || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const champ = {
  border: '1px solid ' + C.border,
  borderRadius: 7,
  padding: '7px 8px',
  fontSize: 12,
  boxSizing: 'border-box',
  width: '100%',
};

/**
 * Soumissions : importer un appel d'offres et en tirer une offre chiffrée
 * depuis le catalogue.
 *
 * Le chemin : réglage du corps de métier → import (fichier SIA 451, PDF ou
 * photos) → lecture et rapprochement par le serveur → vérification du
 * brouillon et du rapport de vigilance → impression, envoi, ou conversion en
 * offre officielle.
 *
 * Comme la maquette, la page est toujours là, au Bâtiment : le réglage du
 * corps de métier et l'explication s'y lisent sans le Pack IA. La lecture, elle,
 * est du pack : sans lui, la carte d'import l'explique et mène à l'abonnement,
 * sans rien demander au serveur de lecture.
 *
 * @param {object} props propriétés de la page
 * @returns {import('react').ReactElement} la page
 */
export function SoumissionsPage({
  onNav,
  soc,
  estMixte,
  univers,
  setUnivers,
  devis,
  setDevis,
  catalogue,
  setCatalogue,
  clients,
}) {
  const { reglageJson } = useReglages();
  const actif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const reglage = reglageJson(CLE_CORPS_METIER, null);
  const cleSession = String(soc?.id ?? soc?.nom ?? '');

  const [filtre, setFiltre] = useState(resumeReglage(reglage) ? 'metier' : 'tout');
  const [choix, setChoix] = useState([]);
  const [fichiers, setFichiers] = useState([]);
  const [etat, setEtat] = useState('');
  const [brouillon, setBrouillonEtat] = useState(() => reprendBrouillon(cleSession));
  const [marge, setMarge] = useState('');
  const [apercu, setApercu] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [apprendre, setApprendre] = useState(true);
  // Comme la maquette, l'import se présente d'abord en un seul bouton ; le
  // choix des postes et des sources se déplie dessous.
  const [importOuvert, setImportOuvert] = useState(false);
  const entreeDocument = useRef(null);
  const entreePhotos = useRef(null);
  const entreeAppareil = useRef(null);

  const setBrouillon = (suivant) =>
    setBrouillonEtat((precedent) => {
      const b = typeof suivant === 'function' ? suivant(precedent) : suivant;
      gardeBrouillon(cleSession, b);
      return b;
    });

  const titre = '📄 Soumissions';
  const shell = (contenu) => (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle={titre}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {contenu}
    </Shell>
  );

  const tauxSociete = tauxTvaParDefaut(soc);
  const totaux = brouillon
    ? totauxSoumission(brouillon.lignes, tauxSociete, brouillon.remisePct)
    : null;
  const client = brouillon ? (clients || []).find((c) => c.id === brouillon.clientId) : null;

  const ajouteFichiers = (liste, remplace) => {
    const nouveaux = Array.from(liste || []);
    if (!nouveaux.length) return;
    setFichiers((p) =>
      remplace ? nouveaux : [...p.filter((f) => f.type.startsWith('image/')), ...nouveaux],
    );
    setEtat('');
  };

  const lit = async () => {
    const erreur = verifieFichiers(fichiers);
    if (erreur) {
      setEtat(erreur);
      return;
    }
    if (filtre === 'choix' && !choix.length) {
      setEtat('Coche au moins un corps de métier, ou importe tout le dossier.');
      return;
    }
    setEtat(EN_COURS);
    try {
      const prets = await prepareFichiers(fichiers);
      const lu = await iaApi.lectureSoumission({
        fichiers: prets,
        filtre,
        ...(filtre === 'choix' ? { metiers: choix } : {}),
      });
      setBrouillon(brouillonDepuisLecture(lu));
      setFichiers([]);
      setEtat('');
    } catch (e) {
      setEtat(e?.status != null ? messageIa(e) : e?.message || messageIa(e));
    }
  };

  const majLigne = (id, champ, valeur) =>
    setBrouillon((b) => ({
      ...b,
      lignes: b.lignes.map((l) => (l.id === id ? { ...l, [champ]: valeur } : l)),
    }));

  const convertit = () => {
    if (!brouillon.lignes.length) return;
    if (totaux.aSaisir > 0) {
      const ok = window.confirm(
        `${totaux.aSaisir} ligne(s) n'ont pas de prix : elles partiront à 0. Convertir quand même ?`,
      );
      if (!ok) return;
    }
    const nouvelles = apprendre ? prestationsAApprendre(brouillon, catalogue) : [];
    if (nouvelles.length && setCatalogue) setCatalogue((p) => [...(p || []), ...nouvelles]);
    const offre = versDevis(brouillon, { devis, soc });
    setDevis((p) => [offre, ...(p || [])]);
    oublieBrouillon();
    setBrouillonEtat(null);
    onNav && onNav('devis');
  };

  if (actif && apercu && brouillon) {
    return shell(
      <div>
        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Btn label="🖨 Imprimer / PDF" onClick={printDoc} full />
          <Btn label="Fermer" ghost color={C.grey} onClick={() => setApercu(false)} />
        </div>
        <ApercuSoumission brouillon={brouillon} totaux={totaux} soc={soc} client={client} />
      </div>,
    );
  }

  const enCours = etat === EN_COURS;
  const resume = resumeReglage(reglage);

  return shell(
    <>
      <CorpsMetierCard />

      {!actif && <ImportVerrouille onNav={onNav} />}

      {actif && (
        <Card style={{ marginBottom: 12 }}>
          <Bar icon="📥" title="Importer une soumission" color={C.teal} sub="Photo, scan ou PDF" />
          <div style={{ padding: 13 }}>
            {!importOuvert && (
              <button
                type="button"
                onClick={() => setImportOuvert(true)}
                style={{
                  display: 'block',
                  width: '100%',
                  border: '2px dashed ' + C.teal,
                  background: C.tealL,
                  color: C.teal,
                  borderRadius: 12,
                  padding: 15,
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
              >
                📷 Importer une soumission (photo / PDF)
              </button>
            )}
            {importOuvert && (
              <>
                <div role="radiogroup" aria-label="Postes à reprendre" style={{ marginBottom: 10 }}>
                  {[
                    ['metier', 'Mon métier' + (resume ? ' — ' + resume : ' (à régler ci-dessus)')],
                    ['tout', 'Tout le dossier'],
                    ['choix', 'Choisir les corps de métier'],
                  ].map(([cle, nom]) => (
                    <label
                      key={cle}
                      style={{
                        display: 'flex',
                        gap: 7,
                        alignItems: 'center',
                        fontSize: 11.5,
                        padding: '4px 0',
                      }}
                    >
                      <input
                        type="radio"
                        name="filtre-soumission"
                        checked={filtre === cle}
                        disabled={cle === 'metier' && !resume}
                        onChange={() => setFiltre(cle)}
                      />
                      {nom}
                    </label>
                  ))}
                  {filtre === 'choix' && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {METIERS_SOUMISSION.map(([cle, nom]) => {
                        const coche = choix.includes(cle);
                        return (
                          <button
                            key={cle}
                            type="button"
                            aria-pressed={coche}
                            onClick={() =>
                              setChoix((p) => (coche ? p.filter((x) => x !== cle) : [...p, cle]))
                            }
                            style={pastille(coche)}
                          >
                            {nom}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 7,
                    marginBottom: 9,
                  }}
                >
                  {[
                    ['📄 PDF / SIA 451', entreeDocument],
                    ['📷 Photographier', entreeAppareil],
                    ['🖼 Photos / scan', entreePhotos],
                  ].map(([nom, ref]) => (
                    <button
                      key={nom}
                      type="button"
                      disabled={enCours}
                      onClick={() => ref.current && ref.current.click()}
                      style={{
                        border: '2px dashed ' + C.teal,
                        background: C.tealL,
                        color: C.teal,
                        borderRadius: 11,
                        padding: '12px 4px',
                        fontSize: 11,
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {nom}
                    </button>
                  ))}
                </div>
              </>
            )}
            <input
              ref={entreeDocument}
              type="file"
              accept={TYPES_DOCUMENT}
              aria-label="Soumission en PDF ou fichier SIA 451"
              style={{ display: 'none' }}
              onChange={(e) => {
                ajouteFichiers(e.target.files, true);
                e.target.value = '';
              }}
            />
            <input
              ref={entreeAppareil}
              type="file"
              accept="image/*"
              capture="environment"
              aria-label="Photographier une page"
              style={{ display: 'none' }}
              onChange={(e) => {
                ajouteFichiers(e.target.files, false);
                e.target.value = '';
              }}
            />
            <input
              ref={entreePhotos}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              aria-label="Pages photographiées ou scannées"
              style={{ display: 'none' }}
              onChange={(e) => {
                ajouteFichiers(e.target.files, false);
                e.target.value = '';
              }}
            />
            {fichiers.length > 0 && (
              <div style={{ fontSize: 10.5, color: C.grey, marginBottom: 9 }}>
                {fichiers.map((f, i) => (
                  <div
                    key={f.name + i}
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>
                      {fichiers.length > 1 ? `Page ${i + 1} — ` : ''}
                      {f.name}
                    </span>
                    <button
                      type="button"
                      aria-label={'Retirer ' + f.name}
                      onClick={() => setFichiers((p) => p.filter((_, j) => j !== i))}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: C.red,
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {(importOuvert || fichiers.length > 0) && (
              <Btn
                label={
                  enCours
                    ? '⏳ Lecture en cours… (jusqu’à quelques minutes)'
                    : '🔍 Lire la soumission'
                }
                color={C.teal}
                full
                disabled={enCours || !fichiers.length}
                onClick={lit}
              />
            )}
            {etat && !enCours && (
              <div
                role="alert"
                style={{ fontSize: 10, color: C.red, marginTop: 7, lineHeight: 1.5 }}
              >
                {etat}
              </div>
            )}
            <div
              style={{
                fontSize: 9.5,
                color: C.muted,
                lineHeight: 1.6,
                background: C.navyXL,
                borderRadius: 10,
                padding: 11,
                marginTop: importOuvert ? 10 : 0,
              }}
            >
              🔒 <b>Lecture automatique avec le Pack IA.</b> L’IA lit la soumission → remplit tes
              prix depuis ton catalogue → te signale les pièges (même cachés dans le texte). La
              soumission est lue puis oubliée par le serveur ; tes prix ne sont jamais transmis au
              service de lecture.
            </div>
          </div>
        </Card>
      )}

      {(!actif || !brouillon) && (
        <Card>
          {/* Sans le pack, la lecture est à venir : le titre de la maquette. */}
          <Bar
            icon="📋"
            title={actif ? 'Comment ça marche' : 'Comment ça marchera'}
            color={C.navy}
          />
          <div style={{ padding: '4px 15px 13px', fontSize: 10.5, color: C.grey, lineHeight: 1.8 }}>
            1. Tu importes (photo / PDF / fichier SIA 451)
            <br />
            2. L’IA lit et extrait chaque poste (désignation, quantité, unité)
            <br />
            3. Elle met tes prix (article connu) ou te laisse taper le prix (inconnu)
            <br />
            4. Elle filtre selon ton métier
            <br />
            5. Elle te signale les pièges (prorata, régie, rabais, options…)
            <br />
            6. Tu ajustes, puis Imprime en PDF ou Envoie
          </div>
        </Card>
      )}

      {actif && brouillon && (
        <Brouillon
          brouillon={brouillon}
          setBrouillon={setBrouillon}
          totaux={totaux}
          majLigne={majLigne}
          clients={clients}
          marge={marge}
          setMarge={setMarge}
          onApercu={() => setApercu(true)}
          envoi={envoi}
          setEnvoi={setEnvoi}
          client={client}
          soc={soc}
          apprendre={apprendre}
          setApprendre={setApprendre}
          aApprendre={prestationsAApprendre(brouillon, catalogue).length}
          onConvertit={convertit}
          onAbandonne={() => {
            if (window.confirm('Abandonner ce brouillon ? La soumission devra être relue.')) {
              oublieBrouillon();
              setBrouillonEtat(null);
            }
          }}
        />
      )}
    </>,
  );
}

/** Le brouillon : rapport de vigilance, lignes, totaux et actions. */
function Brouillon({
  brouillon,
  setBrouillon,
  totaux,
  majLigne,
  clients,
  marge,
  setMarge,
  onApercu,
  envoi,
  setEnvoi,
  client,
  soc,
  apprendre,
  setApprendre,
  aApprendre,
  onConvertit,
  onAbandonne,
}) {
  const [voirEcartes, setVoirEcartes] = useState(false);
  const liens = liensEnvoi(client || {}, messageOffre(brouillon, totaux, soc));
  const reconnus = brouillon.lignes.filter((l) => l.rapprochement && !prixASaisir(l)).length;

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <Bar
          icon="📋"
          title={brouillon.titre || 'Soumission lue'}
          color={C.navy}
          sub={
            (brouillon.maitreOuvrage ? brouillon.maitreOuvrage + ' · ' : '') +
            `${brouillon.lignes.length} ligne(s) · ${reconnus} prix du catalogue · ${totaux.aSaisir} à saisir`
          }
        />
        {brouillon.chapitresEcartes.length > 0 && (
          <div style={{ padding: '8px 13px', fontSize: 10, color: C.grey, lineHeight: 1.5 }}>
            Chapitres laissés de côté par le filtre :{' '}
            {brouillon.chapitresEcartes
              .map((c) => [c.chapitre, c.titre].filter(Boolean).join(' '))
              .join(' · ')}
            {brouillon.ecartes.length > 0 && (
              <button
                type="button"
                onClick={() => setVoirEcartes((v) => !v)}
                style={{
                  marginLeft: 6,
                  border: 'none',
                  background: 'none',
                  color: C.teal,
                  cursor: 'pointer',
                  fontSize: 10,
                }}
              >
                {voirEcartes ? 'masquer' : `voir les ${brouillon.ecartes.length} poste(s)`}
              </button>
            )}
            {voirEcartes &&
              brouillon.ecartes.map((l) => (
                <div
                  key={l.id}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}
                >
                  <span>
                    {l.code} {l.d} ({libelleMetier(l.corpsMetier) || 'métier inconnu'})
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setBrouillon((b) => ({
                        ...b,
                        lignes: [...b.lignes, l],
                        ecartes: b.ecartes.filter((x) => x.id !== l.id),
                      }))
                    }
                    style={{
                      border: 'none',
                      background: 'none',
                      color: C.teal,
                      cursor: 'pointer',
                      fontSize: 10,
                    }}
                  >
                    ＋ reprendre
                  </button>
                </div>
              ))}
          </div>
        )}
      </Card>

      <RapportVigilance alertes={brouillon.alertes} controleTexte={brouillon.controleTexte} />

      <Card style={{ marginBottom: 12 }}>
        <Bar icon="✏️" title="Postes de l'offre" color={C.navy} sub="Tout est modifiable" />
        <div style={{ padding: 10 }}>
          {brouillon.lignes.map((l) => (
            <div
              key={l.id}
              data-testid="ligne-soumission"
              style={{
                border: '1px solid ' + (prixASaisir(l) ? C.amber : C.border),
                background: l.horsTotal ? C.navyXL : '#fff',
                borderRadius: 9,
                padding: 9,
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                <span style={{ fontSize: 9.5, color: C.muted, minWidth: 60 }}>{l.code || '—'}</span>
                <input
                  value={l.d}
                  onChange={(e) => majLigne(l.id, 'd', e.target.value)}
                  aria-label="Désignation"
                  style={{ ...champ, fontWeight: 700 }}
                />
                <button
                  type="button"
                  aria-label={'Supprimer la ligne ' + (l.code || l.d)}
                  onClick={() =>
                    setBrouillon((b) => ({ ...b, lignes: b.lignes.filter((x) => x.id !== l.id) }))
                  }
                  style={{
                    border: 'none',
                    background: 'none',
                    color: C.red,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  🗑
                </button>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.8fr 1.2fr 0.8fr 1.2fr',
                  gap: 5,
                  alignItems: 'center',
                }}
              >
                <input
                  value={l.q}
                  placeholder={l.pm ? 'pm' : 'Qté'}
                  inputMode="decimal"
                  aria-label={'Quantité ' + (l.code || l.d)}
                  onChange={(e) => majLigne(l.id, 'q', e.target.value.replace(',', '.'))}
                  style={champ}
                />
                <input
                  value={l.u}
                  aria-label="Unité"
                  onChange={(e) => majLigne(l.id, 'u', e.target.value)}
                  style={champ}
                />
                <input
                  value={l.p}
                  placeholder="Prix à saisir"
                  inputMode="decimal"
                  aria-label={'Prix unitaire ' + (l.code || l.d)}
                  onChange={(e) => majLigne(l.id, 'p', e.target.value.replace(',', '.'))}
                  style={{ ...champ, borderColor: prixASaisir(l) ? C.amber : C.border }}
                />
                <input
                  value={l.rem || ''}
                  placeholder="Rem. %"
                  inputMode="decimal"
                  aria-label={'Remise ' + (l.code || l.d)}
                  onChange={(e) => majLigne(l.id, 'rem', e.target.value.replace(',', '.'))}
                  style={champ}
                />
                <span style={{ fontSize: 11.5, fontWeight: 800, textAlign: 'right' }}>
                  {l.horsTotal ? 'hors total' : fr(montantLigne(l))}
                </span>
              </div>
              {l.rapprochement && (
                <div style={{ fontSize: 9.5, color: C.green, marginTop: 4 }}>
                  {l.rapprochement.mode === 'code' ? '✓ Code CAN reconnu : ' : '≈ Nom voisin : '}
                  {l.rapprochement.nom}
                  {l.rapprochement.mode === 'nom'
                    ? ` (${Math.round((+l.rapprochement.score || 0) * 100)} %, à vérifier)`
                    : ''}
                  {l.tva != null ? ` · TVA ${l.tva} %` : ''}
                </div>
              )}
              {l.alertes.map((a) => (
                <div key={a} style={{ fontSize: 9.5, color: C.amber, marginTop: 3 }}>
                  ⚠ {a}
                </div>
              ))}
            </div>
          ))}
          <Btn
            label="＋ Ajouter une ligne"
            sm
            ghost
            full
            color={C.teal}
            onClick={() => setBrouillon((b) => ({ ...b, lignes: [...b.lignes, ligneVierge()] }))}
          />
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <Bar icon="🧮" title="Marge, remise et total" color={C.navy} />
        <div style={{ padding: 12, fontSize: 11.5 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input
              value={marge}
              onChange={(e) => setMarge(e.target.value.replace(',', '.'))}
              placeholder="Marge %"
              inputMode="decimal"
              aria-label="Marge à appliquer"
              style={{ ...champ, width: 100 }}
            />
            <Btn
              label="Majorer tous les prix"
              sm
              color={C.navy}
              disabled={!+marge}
              onClick={() => {
                setBrouillon((b) => ({ ...b, lignes: majorePrix(b.lignes, +marge) }));
                setMarge('');
              }}
            />
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
            Remise globale %
            <input
              value={brouillon.remisePct || ''}
              onChange={(e) =>
                setBrouillon((b) => ({ ...b, remisePct: e.target.value.replace(',', '.') }))
              }
              inputMode="decimal"
              aria-label="Remise globale"
              style={{ ...champ, width: 80 }}
            />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
            Client
            <select
              value={brouillon.clientId || ''}
              onChange={(e) =>
                setBrouillon((b) => ({
                  ...b,
                  clientId:
                    e.target.value === ''
                      ? ''
                      : isNaN(+e.target.value)
                        ? e.target.value
                        : +e.target.value,
                }))
              }
              aria-label="Client"
              style={champ}
            >
              <option value="">— à choisir —</option>
              {(clients || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
          </label>
          <div style={{ borderTop: '1px solid ' + C.border, paddingTop: 8 }} aria-label="Totaux">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Total HT</span>
              <b data-testid="total-ht">CHF {fr(totaux.ht)}</b>
            </div>
            {totaux.detail.map((d) => (
              <div
                key={d.taux}
                style={{ display: 'flex', justifyContent: 'space-between', color: C.grey }}
              >
                <span>
                  {libelleTaux(d)} sur {fr(d.ht)}
                </span>
                <span>{fr(d.tva)}</span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              <span>Total TTC</span>
              <span data-testid="total-ttc">CHF {fr(totaux.ttc)}</span>
            </div>
            {totaux.horsTotal > 0 && (
              <div style={{ fontSize: 9.5, color: C.muted }}>
                {totaux.horsTotal} variante(s) / option(s) non comprise(s) dans le total.
              </div>
            )}
            {totaux.aSaisir > 0 && (
              <div style={{ fontSize: 9.5, color: C.amber }}>
                {totaux.aSaisir} prix encore à saisir.
              </div>
            )}
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Btn label="🖨 Imprimer en PDF" color={C.navy} full onClick={onApercu} />
        <Btn label="📤 Envoyer" color={C.teal} full onClick={() => setEnvoi((v) => !v)} />
      </div>
      {envoi && (
        <Card style={{ marginBottom: 8 }}>
          <div style={{ padding: 10 }}>
            {liens
              .filter((l) => l.cle !== 'sms')
              .map((l) => (
                <a
                  key={l.cle}
                  href={l.href}
                  target={l.externe ? '_blank' : undefined}
                  rel="noreferrer"
                  style={{
                    display: 'block',
                    padding: 9,
                    color: C.ink,
                    textDecoration: 'none',
                    fontSize: 12,
                  }}
                >
                  {l.icone} {l.nom} — {l.cible}
                </a>
              ))}
          </div>
        </Card>
      )}
      {aApprendre > 0 && (
        <label
          style={{ display: 'flex', gap: 7, fontSize: 10.5, color: C.grey, margin: '6px 2px 8px' }}
        >
          <input type="checkbox" checked={apprendre} onChange={() => setApprendre(!apprendre)} />
          Ajouter à mon catalogue les {aApprendre} poste(s) chiffré(s) à la main, avec leur code CAN
          (la prochaine soumission les reconnaîtra).
        </label>
      )}
      <Btn
        label={
          termesOffre(soc, { estMag: false }).feminin
            ? '✅ Convertir en offre officielle'
            : '✅ Convertir en devis officiel'
        }
        color={C.green}
        full
        onClick={onConvertit}
      />
      <div style={{ marginTop: 8 }}>
        <Btn
          label="Abandonner ce brouillon"
          sm
          ghost
          danger
          full
          color={C.red}
          onClick={onAbandonne}
        />
      </div>
    </>
  );
}
